"use client";

import { MapPin, Navigation } from "lucide-react";
import dynamic from "next/dynamic";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";

const VenueMap = dynamic(
  () => import("@/components/maps/venue-map").then((mod) => mod.VenueMap),
  {
    ssr: false,
    loading: () => <Skeleton className="h-[220px] w-full rounded-2xl" />,
  },
);

type VenueLocation = {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
};

/**
 * The shared location detail used by venue experiences and activity sessions.
 * A location name is an action, not a dead label: it opens a map, the complete
 * address, and a directions hand-off.
 */
export function VenueLocationDrawer({
  open,
  onOpenChange,
  venue,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venue: VenueLocation;
}) {
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${venue.latitude},${venue.longitude}`;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="z-1000 max-h-[85dvh] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#0f1218] dark:text-white">
        <DrawerHeader className="flex flex-row items-center border-b border-border px-4 py-3 text-left dark:border-white/[0.1]">
          <DrawerTitle className="text-xl leading-tight text-text-primary dark:text-white">
            Location
          </DrawerTitle>
        </DrawerHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <VenueMap
            latitude={venue.latitude}
            longitude={venue.longitude}
            venueName={venue.name}
            address={venue.address}
          />
          <div className="flex items-start gap-3">
            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-text-secondary dark:text-white/58" />
            <div className="min-w-0 space-y-1">
              <p className="text-base font-medium text-text-primary dark:text-white">
                {venue.name}
              </p>
              <p className="text-sm text-text-secondary dark:text-white/70">
                {venue.address}
              </p>
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t border-border bg-surface p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] dark:border-white/[0.1] dark:bg-[#0f1218]">
          <Button
            asChild
            type="button"
            className="h-12 w-full rounded-2xl bg-brand font-semibold text-brand-foreground hover:bg-brand-hover dark:bg-white dark:text-black dark:hover:bg-white/90"
          >
            <a href={directionsUrl} target="_blank" rel="noreferrer">
              <Navigation className="mr-2 h-4 w-4" />
              Get directions
            </a>
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
